<template>
  <div class="course-discussion">
    <div class="toolbar">
      <el-button type="primary" icon="el-icon-edit" @click="openCreateDialog">发起讨论</el-button>
    </div>

    <el-card class="topic-list" v-loading="loading">
      <div v-if="topics.length === 0" class="empty">暂无讨论，点击“发起讨论”开始交流</div>
      <div v-for="topic in topics" :key="topic.id" class="topic-item">
        <div class="topic-header">
          <div class="title">{{ topic.title }}</div>
          <div class="actions">
            <el-button v-if="isTeacher || isCreator(topic.creatorId)" type="text" size="mini" @click="onDeleteTopic(topic)">删除</el-button>
          </div>
        </div>
        <div class="topic-content" v-html="formatContent(topic.content)"></div>
        <div class="topic-meta">
          <span>发起人：{{ topic.creatorName || topic.creatorId }}</span>
          <span style="margin-left:12px;">创建时间：{{ formatTime(topic.createdAt) }}</span>
        </div>

        <div class="reply-box">
          <el-input
            type="textarea"
            :rows="2"
            v-model="topic._replyText"
            placeholder="写下你的回复..."
          />
          <div class="reply-actions">
            <el-button type="primary" size="mini" @click="onCreateReply(topic)">回复</el-button>
          </div>
        </div>

        <div class="replies" v-if="topic._replies && topic._replies.length">
          <div v-for="reply in topic._replies" :key="reply.id" class="reply-item">
            <div class="reply-content" v-html="formatContent(reply.content)"></div>
            <div class="reply-meta">
              <span>回复人：{{ reply.creatorName || reply.creatorId }}</span>
              <span style="margin-left:12px;">{{ formatTime(reply.createdAt) }}</span>
              <el-button v-if="isTeacher || isCreator(reply.creatorId)" type="text" size="mini" @click="onDeleteReply(reply)">删除</el-button>
            </div>
          </div>
        </div>

        <div class="load-replies">
          <el-button type="text" @click="toggleReplies(topic)">{{ topic._showReplies ? '收起回复' : '展开回复' }}</el-button>
        </div>
      </div>
    </el-card>

    <el-dialog title="发起讨论" :visible.sync="createDialogVisible" width="520px">
      <el-form :model="createForm" label-width="68px">
        <el-form-item label="标题">
          <el-input v-model="createForm.title" maxlength="100" show-word-limit />
        </el-form-item>
        <el-form-item label="内容">
          <el-input type="textarea" :rows="6" v-model="createForm.content" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="createDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="creating" @click="onCreateTopic">发 起</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { createTopic, listTopicsByCourse, deleteTopic, createReply, listRepliesByTopic, deleteReply } from '@/api/discussion'
import { getToken } from '@/utils/auth'
import { parseJwt } from '@/utils/jwtUtils'

export default {
  name: 'CourseDiscussion',
  props: {
    courseId: { type: [String, Number], required: true },
    isTeacher: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      topics: [],
      createDialogVisible: false,
      creating: false,
      createForm: { title: '', content: '' },
      currentUserId: null
    }
  },
  created() {
    this.initUser()
    this.fetchTopics()
  },
  methods: {
    initUser() {
      try {
        const token = getToken()
        if (token) {
          const payload = parseJwt(token.replace('Bearer ', ''))
          this.currentUserId = payload && payload.id ? Number(payload.id) : null
        }
      } catch (e) {
        this.currentUserId = null
      }
    },
    isCreator(creatorId) {
      if (!creatorId || !this.currentUserId) return false
      return Number(creatorId) === Number(this.currentUserId)
    },
    formatContent(content) {
      if (!content) return ''
      return String(content).replace(/\n/g, '<br>')
    },
    formatTime(val) {
      if (!val) return ''
      try {
        return new Date(val).toLocaleString()
      } catch (e) {
        return val
      }
    },
    openCreateDialog() {
      this.createForm = { title: '', content: '' }
      this.createDialogVisible = true
    },
    async fetchTopics() {
      if (!this.courseId) return
      this.loading = true
      try {
        const res = await listTopicsByCourse(this.courseId)
        if (res.code === 1) {
          this.topics = (res.data || []).map(t => ({ ...t, _replies: [], _showReplies: false, _replyText: '' }))
        } else {
          this.$message.error(res.msg || '获取讨论列表失败')
        }
      } catch (e) {
        this.$message.error('网络错误，获取讨论失败')
      } finally {
        this.loading = false
      }
    },
    async onCreateTopic() {
      if (!this.createForm.title || !this.createForm.content) {
        this.$message.warning('请填写标题与内容')
        return
      }
      this.creating = true
      try {
        const res = await createTopic({ courseId: this.courseId, title: this.createForm.title, content: this.createForm.content })
        if (res.code === 1) {
          this.$message.success('创建成功')
          this.createDialogVisible = false
          this.fetchTopics()
        } else {
          this.$message.error(res.msg || '创建失败')
        }
      } catch (e) {
        this.$message.error('网络错误，创建失败')
      } finally {
        this.creating = false
      }
    },
    async onDeleteTopic(topic) {
      try {
        await this.$confirm('确定删除该话题及其回复吗？', '提示', { type: 'warning' })
      } catch (_) { return }
      try {
        const res = await deleteTopic(topic.id)
        if (res.code === 1) {
          this.$message.success('删除成功')
          this.fetchTopics()
        } else {
          this.$message.error(res.msg || '删除失败')
        }
      } catch (e) {
        this.$message.error('网络错误，删除失败')
      }
    },
    async toggleReplies(topic) {
      topic._showReplies = !topic._showReplies
      if (topic._showReplies && (!topic._replies || topic._replies.length === 0)) {
        try {
          const res = await listRepliesByTopic(topic.id)
          if (res.code === 1) {
            this.$set(topic, '_replies', res.data || [])
          }
        } catch (e) {}
      }
    },
    async onCreateReply(topic) {
      if (!topic._replyText) {
        this.$message.warning('请输入回复内容')
        return
      }
      try {
        const res = await createReply({ topicId: topic.id, parentId: null, content: topic._replyText })
        if (res.code === 1) {
          this.$message.success('回复成功')
          topic._replyText = ''
          if (topic._showReplies) {
            const listRes = await listRepliesByTopic(topic.id)
            if (listRes.code === 1) this.$set(topic, '_replies', listRes.data || [])
          }
        } else {
          this.$message.error(res.msg || '回复失败')
        }
      } catch (e) {
        this.$message.error('网络错误，回复失败')
      }
    },
    async onDeleteReply(reply) {
      try {
        await this.$confirm('确定删除该回复吗？', '提示', { type: 'warning' })
      } catch (_) { return }
      try {
        const res = await deleteReply(reply.id)
        if (res.code === 1) {
          this.$message.success('删除成功')
          const topic = this.topics.find(t => t.id === reply.topicId)
          if (topic) {
            const listRes = await listRepliesByTopic(topic.id)
            if (listRes.code === 1) this.$set(topic, '_replies', listRes.data || [])
          }
        } else {
          this.$message.error(res.msg || '删除失败')
        }
      } catch (e) {
        this.$message.error('网络错误，删除失败')
      }
    }
  }
}
</script>

<style scoped>
.toolbar { margin-bottom: 12px; }
.topic-list { padding: 8px; }
.empty { color: #909399; text-align: center; padding: 24px 0; }
.topic-item { border-bottom: 1px solid #ebeef5; padding: 12px 4px; }
.topic-header { display: flex; justify-content: space-between; align-items: center; }
.title { font-weight: 600; font-size: 16px; }
.topic-content { margin-top: 6px; line-height: 1.6; color: #303133; }
.topic-meta { margin-top: 6px; color: #909399; font-size: 12px; }
.reply-box { margin-top: 10px; }
.reply-actions { margin-top: 6px; text-align: right; }
.replies { margin-top: 10px; background: #fafafa; padding: 10px; border-radius: 4px; }
.reply-item { border-bottom: 1px dashed #e4e7ed; padding: 6px 0; }
.reply-item:last-child { border-bottom: none; }
.reply-content { color: #606266; }
.reply-meta { display: flex; justify-content: space-between; color: #909399; font-size: 12px; }
.load-replies { margin-top: 6px; }
</style>
